import { useWindowVirtualizer } from "@tanstack/react-virtual"
import { Typography } from "@mui/material"
import { CSSProperties, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { TranslationKey } from "../../../../../localization/translations"
import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"

import { ResourceUsageRow } from "./ResourceUsageRow"
import { UsageCard } from "./UsageCard"
import type { ResourceTypeGroupViewModel } from "./tenantUsageViewModel"

const SECTION_TITLE_KEY: Record<ResourceType | "UNKNOWN", TranslationKey> = {
    [ResourceType.ROOM]: "service-center-usage-per-tenant-section-title-room",
    [ResourceType.MOBILE_AV]: "service-center-usage-per-tenant-section-title-mobile-av",
    [ResourceType.CAR]: "service-center-usage-per-tenant-section-title-car",
    UNKNOWN: "service-center-usage-per-tenant-section-title-unknown",
}

// Rough per-row size estimates (px); dynamic measurement corrects any drift.
const RESOURCE_ESTIMATE = 60
const EXPANDED_EXTRA = 140

const useStyles = makeStyles()((theme) => ({
    title: {
        fontWeight: 700,
        color: theme.palette.tailwind.slate[800],
        marginBottom: ".5rem",
    },
    list: {
        position: "relative",
        width: "100%",
        height: "var(--v-total-height)",
    },
    item: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        transform: "translateY(var(--v-translate))",
    },
}))

type ResourceTypeCardProps = Readonly<{
    group: ResourceTypeGroupViewModel
    expandedIds: ReadonlySet<number>
    onToggle: (resourceId: number) => void
}>

export function ResourceTypeCard({ group, expandedIds, onToggle }: ResourceTypeCardProps) {
    const { classes } = useStyles()
    const listRef = useRef<HTMLDivElement>(null)
    const [scrollMargin, setScrollMargin] = useState(0)

    const estimateSize = useCallback(
        (index: number) => {
            const resource = group.resources[index]
            return expandedIds.has(resource.resourceId) ? RESOURCE_ESTIMATE + EXPANDED_EXTRA : RESOURCE_ESTIMATE
        },
        [group.resources, expandedIds],
    )

    const virtualizer = useWindowVirtualizer({
        count: group.resources.length,
        estimateSize,
        overscan: 6,
        scrollMargin,
        getItemKey: (index) => group.resources[index].resourceId,
    })

    // The window virtualizer needs the list's document offset. It shifts when content above
    // (or a sibling card expanding) resizes, so re-measure after every render and on resize.
    useLayoutEffect(() => {
        const next = listRef.current?.offsetTop ?? 0
        setScrollMargin((prev) => (prev === next ? prev : next))
    })

    useEffect(() => {
        const onResize = () => {
            const next = listRef.current?.offsetTop ?? 0
            setScrollMargin((prev) => (prev === next ? prev : next))
        }
        window.addEventListener("resize", onResize)
        return () => window.removeEventListener("resize", onResize)
    }, [])

    // CSS custom properties: dynamic virtualization geometry (DOM typing gap for custom props).
    const totalStyle = { "--v-total-height": `${virtualizer.getTotalSize()}px` } as CSSProperties

    return (
        <UsageCard>
            <LocalizedStrict id={SECTION_TITLE_KEY[group.resourceType]}>
                <Typography variant="h6" className={classes.title}>
                    {group.resourceType}
                </Typography>
            </LocalizedStrict>
            <div ref={listRef} className={classes.list} style={totalStyle}>
                {virtualizer.getVirtualItems().map((virtualItem) => {
                    const resource = group.resources[virtualItem.index]
                    const itemStyle = {
                        "--v-translate": `${virtualItem.start - virtualizer.options.scrollMargin}px`,
                    } as CSSProperties

                    return (
                        <div
                            key={virtualItem.key}
                            data-index={virtualItem.index}
                            ref={virtualizer.measureElement}
                            className={classes.item}
                            style={itemStyle}
                        >
                            <ResourceUsageRow
                                resource={resource}
                                isExpanded={expandedIds.has(resource.resourceId)}
                                onToggle={onToggle}
                            />
                        </div>
                    )
                })}
            </div>
        </UsageCard>
    )
}
